/**
 * Вкус: что этот слушатель вообще любит, поверх того, что просит погода.
 *
 * Зачем. История прослушиваний знала только id трека. В каталоге на сотни
 * тысяч вещей такая память не переносится ни на что: пропустив три вещи
 * подряд, следующий подбор об этом не узнаёт — трек-то каждый раз другой.
 * Здесь отказ и прослушивание оседают на ТЕГАХ, а теги повторяются.
 *
 * Намеренно слабое. Продукт озвучивает погоду, а не крутит любимое: вклад
 * вкуса в оценку ограничен так, чтобы он решал между близкими кандидатами и
 * не мог перебить попадание в погоду. Иначе через неделю приложение играло бы
 * одно и то же независимо от того, что за окном.
 *
 * Хранится только в браузере (см. lib/taste.ts), никуда не отправляется.
 */
import type { PlayKind } from "./history";
import { VERIFIED_TAGS } from "./vocab";

export interface Taste {
  /** Когда запись последний раз трогали — от неё считается старение. */
  at: number;
  tags: Record<string, number>;
}

export const EMPTY_TASTE: Taste = { at: 0, tags: {} };

const DAY = 86_400_000;

/**
 * Полураспад дольше, чем у усталости от трека (10 дней): «надоело» проходит
 * быстро, а «не моё» держится. Но и оно не навсегда — вкус меняется.
 */
const HALF_LIFE = 21 * DAY;

/** Пропуск весит больше прослушивания: это прямой отказ, а не просто факт. */
const DELTA: Record<PlayKind, number> = { played: 0.3, sampled: 0.05, skipped: -0.4 };

/** Потолок по модулю: дальше мнение не крепчает, сколько ни повторяй. */
const LIMIT = 1.5;

/** Сколько тегов помним. Больше словаря каталога хранить незачем. */
const MAX_TAGS = 64;

const clamp = (v: number) => (v < -LIMIT ? -LIMIT : v > LIMIT ? LIMIT : v);

function decayed(taste: Taste, now: number): Record<string, number> {
  if (!taste.at || now <= taste.at) return { ...taste.tags };
  const factor = Math.pow(0.5, (now - taste.at) / HALF_LIFE);
  const out: Record<string, number> = {};
  for (const [tag, v] of Object.entries(taste.tags)) out[tag] = v * factor;
  return out;
}

/** Только теги каталога: из `subject` архива прилетает что угодно. */
const known = (tags: string[]) =>
  [...new Set(tags.map((t) => t.trim().toLowerCase()))].filter((t) => VERIFIED_TAGS.has(t));

export function learn(taste: Taste, tags: string[], kind: PlayKind, now: number): Taste {
  const next = decayed(taste, now);
  const list = known(tags);
  if (list.length) {
    // Делим на корень числа тегов: у вещи с восемью тегами каждый отдельный
    // виноват меньше, чем единственный тег у вещи с одним.
    const share = DELTA[kind] / Math.sqrt(list.length);
    for (const tag of list) next[tag] = clamp((next[tag] ?? 0) + share);
  }

  const trimmed = Object.entries(next)
    .sort((a, b) => Math.abs(b[1]) - Math.abs(a[1]))
    .slice(0, MAX_TAGS);
  return { at: now, tags: Object.fromEntries(trimmed) };
}

/**
 * Насколько этот набор тегов человеку по душе: от −1.5 до +1.5, ноль — нечего
 * сказать.
 *
 * Делится не на число известных тегов, а минимум на два: одна метка не должна
 * выносить приговор всему треку.
 */
export function tasteScore(taste: Taste, tags: string[], now: number): number {
  const list = known(tags);
  if (!list.length) return 0;
  const values = decayed(taste, now);
  let sum = 0;
  for (const tag of list) sum += values[tag] ?? 0;
  return sum / Math.max(2, list.length);
}
